import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { HyperText } from '../components/HyperText';
import { WordFadeIn } from '../components/ui/WordFadeIn';
import { motion } from 'framer-motion';
import { FadeIn } from '../components/animations/FadeIn';
import { SlideIn } from '../components/animations/SlideIn';
import { ScrollReveal } from '../components/animations/ScrollReveal';
import { ShieldCheck, Target, Heart, Clock, School, Building, Users, AlertTriangle, ArrowRight } from 'lucide-react';
import cn from 'classnames';
import { ImageFadeIn } from '../components/animations/ImageFadeIn';
// Lazy load the WhatWeDoFeature component
const WhatWeDoFeature = lazy(() => import('../components/WhatWeDoFeature'));
const HomePage = () => {
  return <div className="w-full" data-id="element-457">
      {/* Hero Section */}
      <div className="w-full relative min-h-screen" data-id="element-458">
        <div className="absolute inset-0 w-full h-full overflow-hidden" data-id="element-459">
          <div className="absolute inset-0 bg-israel-blue/30 mix-blend-overlay z-10" data-id="element-460"></div>
          <div className="absolute inset-0 bg-blue-900/10 z-10" data-id="element-461"></div>
          <iframe src="https://player.vimeo.com/video/1102199157?badge=0&autopause=0&player_id=0&app_id=58479&background=1&autoplay=1&loop=1&muted=1" className="absolute w-[400%] sm:w-[200%] md:w-full h-full -left-[150%] sm:-left-[50%] md:left-0 top-0 object-cover" style={{
          pointerEvents: 'none',
          transform: 'scale(1.5)',
          transformOrigin: 'center center'
        }} loading="lazy" fetchpriority="high" importance="high" title="israel site final" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" frameBorder="0" data-id="element-462"></iframe>
        </div>
        <div className="relative z-20 flex flex-col justify-end h-screen pb-16 sm:pb-24 px-4 sm:px-6" data-id="element-463">
          <div className="max-w-7xl mx-auto w-full" data-id="element-464">
            <div className="mx-auto text-center" data-id="element-465">
              <FadeIn delay={0.2} data-id="element-466">
                <div className="flex justify-center mb-6 sm:mb-8" data-id="element-467">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/8B1MH4cHQVHbhD8ECUMDMu/whiteictc.png" alt="Israeli Civilian Training Foundation Logo" className="h-20 sm:h-24 w-auto" data-id="element-468" />
                </div>
              </FadeIn>
              <SlideIn delay={0.4} data-id="element-469">
                <HyperText text="FORTIFYING ISRAEL, ONE FAMILY AT A TIME" className="text-white text-xl sm:text-2xl md:text-3xl mb-3 sm:mb-4" duration={1200} data-id="element-470" />
              </SlideIn>
              <FadeIn delay={0.6} data-id="element-471">
                <WordFadeIn words="The Israeli Civilian Training Fund (ICTF) equips Israeli families and communities with the practical training needed to survive, defend, and protect what matters most." className="text-base sm:text-lg md:text-xl text-gray-100 mb-8 leading-relaxed font-light max-w-2xl mx-auto px-2 sm:px-0" delay={0.8} variants={{
                hidden: {
                  opacity: 0,
                  y: 10
                },
                visible: (i: number) => ({
                  y: 0,
                  opacity: 1,
                  transition: {
                    delay: 0.4 + i * 0.02,
                    duration: 0.2
                  }
                })
              }} data-id="element-472" />
              </FadeIn>
              <FadeIn delay={1} data-id="element-473">
                <motion.div whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} transition={{
                duration: 0.2
              }} data-id="element-474">
                  <Link to="/support" className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-israel-blue text-white font-medium rounded-lg hover:bg-israel-blue-light transition-all duration-300" data-id="element-475">
                    Support the Mission
                  </Link>
                </motion.div>
              </FadeIn>
              <FadeIn delay={1.2} data-id="element-476">
                <p className="text-xl sm:text-2xl md:text-3xl text-white font-bold italic mt-4" data-id="element-477">
                  "Protecting a nation, Preserving a generation"
                </p>
              </FadeIn>
            </div>
          </div>
        </div>
      </div>
      {/* Urgent Need Section */}
      <ScrollReveal animation="fade" data-id="element-478">
        <div className="bg-white py-16 sm:py-24" data-id="element-479">
          <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-480">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-481">
              <SlideIn direction="left" data-id="element-482">
                <div data-id="element-483">
                  <h2 className="text-3xl sm:text-4xl font-heading text-gray-900 mb-6" data-id="element-484">
                    When Minutes Matter, Training Saves Lives
                  </h2>
                  <div className="space-y-4 text-gray-700 mb-8" data-id="element-485">
                    <p className={cn('text-lg leading-relaxed')} data-id="element-486">
                      In October 2023, Israel faced its deadliest attack in
                      decades. Communities across Israel—particularly those near
                      borders—found themselves isolated and vulnerable as
                      security forces were stretched beyond capacity.
                    </p>
                    <p className={cn('text-lg leading-relaxed')} data-id="element-487">
                      Today, Israel continues to face unprecedented security
                      challenges on multiple fronts. With IDF forces deployed
                      and reserves repeatedly mobilized, the threat to Israeli
                      civilians has reached a critical level. We are equipping
                      everyday Israelis with the training, tools, and mindset
                      needed to protect their families, support their
                      communities, and stand resilient in times of crisis. When
                      seconds matter and help is minutes away, trained civilians
                      become the first line of defense.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4" data-id="element-488">
                    <motion.div whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.95
                  }} data-id="element-489">
                      <Link to="/about" className="inline-flex items-center px-6 py-3 bg-israel-blue text-white font-medium rounded-lg hover:bg-israel-blue-light transition-all duration-300" data-id="element-490">
                        Learn More
                        <motion.span animate={{
                        x: [0, 5, 0]
                      }} transition={{
                        duration: 1,
                        repeat: Infinity
                      }} data-id="element-491">
                          <ArrowRight className="ml-2 w-4 h-4" data-id="element-492" />
                        </motion.span>
                      </Link>
                    </motion.div>
                    <Link to="/training-center" className="inline-flex items-center px-6 py-3 border border-israel-blue text-israel-blue font-medium rounded-lg hover:bg-blue-50 transition-colors" data-id="element-493">
                      Explore Our Solution
                    </Link>
                  </div>
                </div>
              </SlideIn>
              <SlideIn direction="right" data-id="element-494">
                <div className="relative" data-id="element-495">
                  <motion.div animate={{
                  scale: [1, 1.02, 1],
                  rotate: [0, 1, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="absolute -top-6 -left-6 w-24 h-24 bg-blue-100 rounded-full opacity-50" data-id="element-496" />
                  <div className="relative z-10 rounded-xl overflow-hidden shadow-xl" data-id="element-497">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9cpDLWMPsawsEL33rME46D/F230719AM20.jpg" alt="Armed personnel in training scenario" className="w-full h-auto" data-id="element-498" />
                  </div>
                  <motion.div animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, -1, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.5
                }} className="absolute -bottom-6 -right-6 w-32 h-32 bg-israel-blue/10 rounded-full" data-id="element-499" />
                </div>
              </SlideIn>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Key Stats Section */}
      <ScrollReveal animation="fade" data-id="element-500">
        <div className="bg-gray-50 py-16 sm:py-24" data-id="element-501">
          <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-502">
            <div className="text-center mb-12" data-id="element-503">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-504">
                The Challenge & Our Goals
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-505">
                Israel faces unprecedented security threats that demand a new
                approach to civilian preparedness
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-506">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-507">
                <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 transform transition-all hover:scale-105 hover:shadow-lg" data-id="element-508">
                  <div className="flex items-start" data-id="element-509">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-510">
                      <AlertTriangle className="w-6 h-6 text-red-500" data-id="element-511" />
                    </div>
                    <div data-id="element-512">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-513">
                        Urgent Need
                      </h3>
                      <p className="text-gray-600" data-id="element-514">
                        Israeli civilians, particularly in border communities,
                        face ongoing security threats with limited access to
                        training
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-515">
                <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 transform transition-all hover:scale-105 hover:shadow-lg" data-id="element-516">
                  <div className="flex items-start" data-id="element-517">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-518">
                      <Users className="w-6 h-6 text-israel-blue" data-id="element-519" />
                    </div>
                    <div data-id="element-520">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-521">
                        Training Goal
                      </h3>
                      <p className="text-gray-600" data-id="element-522">
                        Our mission is to train 50,000 civilians annually
                        through free, comprehensive security programs
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.3} data-id="element-523">
                <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 transform transition-all hover:scale-105 hover:shadow-lg" data-id="element-524">
                  <div className="flex items-start" data-id="element-525">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-526">
                      <Building className="w-6 h-6 text-green-600" data-id="element-527" />
                    </div>
                    <div data-id="element-528">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-529">
                        Project Cost
                      </h3>
                      <p className="text-gray-600" data-id="element-530">
                        $14.1M needed to build Israel's first dedicated civilian
                        training center
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
            <div className="mt-12 text-center" data-id="element-531">
              <Link to="/roadmap" className="inline-flex items-center text-israel-blue hover:underline font-medium" data-id="element-532">
                View our implementation timeline
                <ArrowRight className="ml-2 w-4 h-4" data-id="element-533" />
              </Link>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Our Approach Section */}
      <ScrollReveal animation="fade" data-id="element-534">
        <div className="bg-white py-16 sm:py-24" data-id="element-535">
          <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-536">
            <div className="text-center mb-16" data-id="element-537">
              <div className="inline-flex items-center justify-center mb-4" data-id="element-538">
                <Target className="w-8 h-8 text-israel-blue mr-3" data-id="element-539" />
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900" data-id="element-540">
                  Our Approach
                </h2>
              </div>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-541">
                Comprehensive training designed to equip Israeli civilians with
                life-saving skills
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-542">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-543">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300" data-id="element-544">
                  <div className="flex justify-center mb-6" data-id="element-545">
                    <div className="w-16 h-16 rounded-full bg-israel-blue/10 flex items-center justify-center" data-id="element-546">
                      <ShieldCheck className="w-8 h-8 text-israel-blue" data-id="element-547" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center" data-id="element-548">
                    Prepare
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-549">
                    Uniform, legally grounded training in self-defense,
                    emergency medicine, and crisis response.
                  </p>
                  <ul className="space-y-2" data-id="element-550">
                    <li className="flex items-start" data-id="element-551">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-552">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-553"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-554">
                        Situational awareness training
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-555">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-556">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-557"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-558">
                        Weapon proficiency & safety
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-559">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-560">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-561"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-562">
                        Emergency response protocols
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-563">
                    <Link to="/programs" className="text-israel-blue hover:underline text-sm font-medium flex items-center" data-id="element-564">
                      Learn more
                      <ArrowRight className="ml-1 w-3 h-3" data-id="element-565" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-566">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300" data-id="element-567">
                  <div className="flex justify-center mb-6" data-id="element-568">
                    <div className="w-16 h-16 rounded-full bg-israel-blue/10 flex items-center justify-center" data-id="element-569">
                      <School className="w-8 h-8 text-israel-blue" data-id="element-570" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center" data-id="element-571">
                    Equip
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-572">
                    State-of-the-art facilities providing hands-on training in
                    realistic environments.
                  </p>
                  <ul className="space-y-2" data-id="element-573">
                    <li className="flex items-start" data-id="element-574">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-575">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-576"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-577">
                        Live-fire ranges with moving targets
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-578">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-579">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-580"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-581">
                        3 MILO Range simulators for scenario training
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-582">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-583">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-584"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-585">
                        Trauma manikins for medical training
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-586">
                    <Link to="/training-center" className="text-israel-blue hover:underline text-sm font-medium flex items-center" data-id="element-587">
                      Explore our facilities
                      <ArrowRight className="ml-1 w-3 h-3" data-id="element-588" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.3} data-id="element-589">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300" data-id="element-590">
                  <div className="flex justify-center mb-6" data-id="element-591">
                    <div className="w-16 h-16 rounded-full bg-israel-blue/10 flex items-center justify-center" data-id="element-592">
                      <Heart className="w-8 h-8 text-israel-blue" data-id="element-593" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center" data-id="element-594">
                    Protect
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-595">
                    Building community resilience through comprehensive training
                    and preparation.
                  </p>
                  <ul className="space-y-2" data-id="element-596">
                    <li className="flex items-start" data-id="element-597">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-598">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-599"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-600">
                        Home defense strategies
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-601">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-602">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-603"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-604">
                        Life-saving medical intervention
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-605">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-606">
                        <div className="w-2 h-2 bg-israel-blue rounded-full" data-id="element-607"></div>
                      </div>
                      <span className="text-sm text-gray-600" data-id="element-608">
                        Community coordination techniques
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-609">
                    <Link to="/about" className="text-israel-blue hover:underline text-sm font-medium flex items-center" data-id="element-610">
                      About our mission
                      <ArrowRight className="ml-1 w-3 h-3" data-id="element-611" />
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Timeline Section */}
      {/* Call to Action */}
      <ScrollReveal animation="fade" data-id="element-612">
        <div className="bg-israel-blue py-16 sm:py-24" data-id="element-613">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-white" data-id="element-614">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-id="element-615">
              Join Our Mission to Protect Israeli Communities
            </h2>
            <p className="text-xl opacity-90 mb-10 max-w-3xl mx-auto font-light" data-id="element-616">
              Every contribution helps us build a safer, more resilient Israel
              through civilian training and preparedness.
            </p>
            <div className="flex flex-wrap justify-center gap-6" data-id="element-617">
              <a href="https://donate.stripe.com/7sY5kw7P1eO24x90y314401" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-white text-israel-blue font-semibold rounded-lg hover:bg-gray-100 transition-colors text-lg shadow-lg" data-id="element-618">
                Donate Now
              </a>
              <Link to="/support" className="inline-block px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors text-lg" data-id="element-619">
                Support Our Mission
              </Link>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>;
};
export default HomePage;